import type { DateFormatApi, NumberFormatApi } from '../api/models/userPreferenceDefaults';
import { getGlobalPreferences } from '../contexts/PreferencesContext';

/**
 * Format a date according to the specified date format preference
 * @param date - Date to format
 * @param dateFormat - Date format preference (optional, uses global preference if not provided)
 * @param options - Optional formatting options (day, month, year, etc.)
 * @returns Formatted date string
 */
export function formatDate(
  date: Date,
  dateFormat?: DateFormatApi,
  options?: {
    day?: 'numeric' | '2-digit';
    month?: 'numeric' | '2-digit' | 'short' | 'long';
    year?: 'numeric' | '2-digit';
    hour?: '2-digit';
    minute?: '2-digit';
    hour12?: boolean;
  }
): string {
  // Use provided format or fall back to global preference
  const format = dateFormat || getGlobalPreferences()?.dateFormat || 'DD/MM/YYYY';
  
  // Default to full date if no options provided
  const formatOptions = options || {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };

  // Map date format preference to locale
  const locale = getLocaleForDateFormat(format);
  
  return new Intl.DateTimeFormat(locale, formatOptions).format(date);
}

/**
 * Format currency according to the specified number format preference
 * @param amount - Amount to format
 * @param currency - Currency code (default: GBP)
 * @param options - Optional formatting options
 * @param numberFormat - Number format preference (optional, uses global preference if not provided)
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency: string = 'GBP',
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    compact?: boolean;
  },
  numberFormat?: NumberFormatApi
): string {
  // Use provided format or fall back to global preference
  const format = numberFormat || getGlobalPreferences()?.numberFormat || 'comma-dot';
  
  const formatOptions: Intl.NumberFormatOptions = {
    style: 'currency' as const,
    currency,
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 0,
  };

  // Add compact notation if requested
  if (options?.compact) {
    formatOptions.notation = 'compact';
    formatOptions.compactDisplay = 'short';
    formatOptions.minimumFractionDigits = 0;
    formatOptions.maximumFractionDigits = 1;
  }

  // Map number format preference to locale
  const locale = getLocaleForNumberFormat(format);
  
  return new Intl.NumberFormat(locale, formatOptions).format(amount);
}

/**
 * Format a number according to the specified number format preference (without currency symbol)
 * @param amount - Number to format
 * @param numberFormat - Number format preference (optional, uses global preference if not provided)
 * @param options - Optional formatting options
 * @returns Formatted number string
 */
export function formatNumber(
  amount: number,
  numberFormat?: NumberFormatApi,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  // Use provided format or fall back to global preference
  const format = numberFormat || getGlobalPreferences()?.numberFormat || 'comma-dot';
  
  const formatOptions = {
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 0,
  };

  // Map number format preference to locale
  const locale = getLocaleForNumberFormat(format);
  
  return new Intl.NumberFormat(locale, formatOptions).format(amount);
}

/**
 * Map date format preference to appropriate locale
 */
function getLocaleForDateFormat(dateFormat: DateFormatApi): string {
  switch (dateFormat) {
    case 'DD/MM/YYYY':
      return 'en-GB';
    case 'MM/DD/YYYY':
      return 'en-US';
    case 'YYYY-MM-DD':
      return 'sv-SE';
    default:
      return 'en-GB';
  }
}

/**
 * Map number format preference to appropriate locale
 */
function getLocaleForNumberFormat(numberFormat: NumberFormatApi): string {
  switch (numberFormat) {
    case 'comma-dot':
      return 'en-GB';
    case 'dot-comma':
      return 'de-DE';
    case 'none-dot':
      return 'en-IN';
    default:
      return 'en-GB';
  }
}
